import { toast, ToastContainer } from 'react-toastify'
import * as Yup from "yup"
import { useFormik } from "formik"
import { request } from "../../services/request"
import { Form } from "react-bootstrap"
import PrimaryButton from '../../components/subComponents/PrimaryButton'
import SecondaryButton from '../../components/subComponents/SecondaryButton'
import { CustomerDetail, Customernterface, LocationData, LocationDataResponse, SendNotifications, SensorsListData, SensorsListResponse, SubscriptionData, UpdateSubscriptionResponse } from '../../services/authinterface'
import React, { useState } from 'react'

// Define the possible notification targets with numerical values (0 to 4)
const notificationTargets = [
    { value: "0", label: 'All Users' },
    { value: "1", label: 'Specific Users' },
    { value: "2", label: 'Owners' },
    { value: "3", label: 'Users at location' },
    { value: "4", label: 'Users near sensors' },
];

const SendNotification = () => {
    const [loading, setLoading] = useState(false)
    const [customers, setCustomers] = useState<CustomerDetail[]>([])
    const [locations, setLocations] = useState<LocationData[]>([])
    const [sensors, setSensors] = useState<SensorsListData[]>([])
    const [owners, setOwners] = useState<SubscriptionData[]>([])

    // --- Validation Schema (Now correct) ---
    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Please enter Title"),
        detail: Yup.string().trim().required("Please enter Detail"),
        user_type: Yup.string().oneOf(
            notificationTargets.map(t => t.value),
            "Please select a valid notification user type"
        ).required("Please select a user type"),
        user_id: Yup.string().when('user_type', {
            is: "1",
            then: (schema) => schema.required("Please select a Specific User"),
            otherwise: (schema) => schema.notRequired(),
        }),
        location_id: Yup.string().when('user_type', {
            is: "3",
            then: (schema) => schema.required("Please select a Location"),
            otherwise: (schema) => schema.notRequired(),
        }),
        sensor_id: Yup.string().when('user_type', {
            is: "4",
            then: (schema) => schema.required("Please select a Location"),
            otherwise: (schema) => schema.notRequired(),
        }),
        owner_id: Yup.string().when('user_type', {
            is: "2",
            then: (schema) => schema.required("Please select a Owner"),
            otherwise: (schema) => schema.notRequired(),
        }),
        sendTo : Yup.boolean(),
        is_owner: Yup.boolean(),
        is_admin: Yup.boolean(),
        is_viewer: Yup.boolean(),
        distance: Yup.number().when('user_type', {
            is: "4",
            then: (schema) => schema.required("Please enter Distance"),
            otherwise: (schema) => schema.notRequired(),
        })
    })

    const fetchCustomers = React.useCallback(async () => {
        try {
            // ... API call to get customers ...
            const params = {
                page: 1,
                limit: 1000,
                search: "",
                sort: "DESC",
                sortCol: "createdAt",
                userType: "",
                dateFilter: "",
                userRole: ""
            }
            const response = await request<Customernterface>({
                url: "/adminCustomers/getCustomers",
                method: "POST",
                data: params,
            });

            if (response.success) {
                // Assuming CustomerDetail has an 'id' or '_id' field for the value
                setCustomers(response.data);
            } else {
                toast.error("Failed to fetch customers list: " + response.message);
            }
        } catch (error: any) {
            toast.error(`Error fetching customers: ${error.message}`);
        }
    }, [])

    const fetchLocationData = React.useCallback(async () => {
        try {
            const params = {
                page: 1,
                limit: 100000,
                search: "",
                sort: "DESC",
                sortCol: "createdAt",
                status: "",
                ownerId: "",
                city: "",
                state: ""
            }
            const response = await request<LocationDataResponse>({
                url: "/adminCommon/getLocations",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setLocations(response.data)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`Error: ${error.message}`)
        }
    }, [])

    const fetchSensorsData = React.useCallback(async () => {
        try {
            const params = {
                page: 1,
                limit: 100000,
                search: "",
                sort: "DESC",
                sortCol: "createdAt",
                status: "",
                ownerId: "",
                addressId: "",
                startDate: "",
                endDate: ""
            }
            const response = await request<SensorsListResponse>({
                url: "/adminCommon/getAllSensors",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setSensors(response.data)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`Error: ${error.message}`)
        }
    }, [])

    const fetchOwners = React.useCallback(async () => {
        try {
            const params = {
                page: 1,
                limit: 100000,
                search: "",
                sort: "DESC",
                sortCol: "createdAt",
            }
            const response = await request<UpdateSubscriptionResponse>({
                url: "/adminCustomers/allOwners",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setOwners(response.data)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`Error: ${error.message}`)
        }
    }, [])

    const formik = useFormik({
        initialValues: {
            title: "",
            detail: "",
            user_type: notificationTargets[0].value,
            user_id: "",
            location_id: "",
            sensor_id: "",
            owner_id: "",
            sendTo: false,
            is_owner: true,
            is_admin: true,
            distance: 1,
            is_viewer: true
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true)
                const payload: any = {
                    title: values.title.trim(),
                    detail: values.detail.trim(),
                    user_type: values.user_type,
                };

                switch (values.user_type) {
                    case "1":
                        payload.user_id = values.user_id;
                        break;
                    case "2":
                        payload.user_id = values.owner_id;
                        payload.sendTo = values.sendTo;
                        break;
                    case "3":
                        payload.location_id = values.location_id;
                        payload.is_owner = values.is_owner;
                        payload.is_admin = values.is_admin;
                        payload.is_viewer = values.is_viewer;
                        payload.distance = values.distance;
                        break;
                    case "4":
                        payload.sensor_id = values.sensor_id;
                        payload.distance = values.distance;
                        payload.is_owner = values.is_owner;
                        payload.is_admin = values.is_admin;
                        payload.is_viewer = values.is_viewer;
                        break;
                    default:
                        break;
                }
                const response = await request<SendNotifications>({
                    url: `/adminCommon/sendNotificationToAll`,
                    method: "POST",
                    data: payload,
                })
                if (response.success) {
                    toast.success(response.message)
                    formik.resetForm()
                } else {
                    toast.error(response.message)
                }
            } catch (error: any) {
                toast.error(`${error.message}`)
            } finally {
                setLoading(false)
            }
        },
    })

    React.useEffect(() => {
        if (formik.values.user_type === "1") {
            fetchCustomers();
        }
        else if (formik.values.user_type === "2") {
            fetchOwners()
        }
        else if (formik.values.user_type === "3") {
            fetchLocationData()
        }
        else if (formik.values.user_type === "4") {
            fetchSensorsData()
        }
    }, [fetchOwners, fetchSensorsData, fetchLocationData, fetchCustomers, formik.values.user_type]);

    return (
        <section className='dashboard-section'>
            <ToastContainer position='top-right' autoClose={2000} theme='light' />
            <div className='container'>
                <div className='row justify-content-center'>
                    <div className='col-12'>
                        <div className='d-flex gap-1 mb-2'>
                            <div className='dashboard-table-title-section title-line w-100'>
                                <h2>Send Notification</h2>
                            </div>
                        </div>
                    </div>
                    <div className='col-xl-8 col-lg-10 col-md-10 col-12 mt-4 w-100'>
                        <form onSubmit={formik.handleSubmit} className='customer-edit-form'>
                            <div className='row'>
                                <div className='col-12 col-sm-6'>
                                    <div className='form-group login-input-section'>
                                        <label htmlFor='title'>Title</label>
                                        <Form.Control
                                            type='text'
                                            className='form-control'
                                            id='title'
                                            name='title'
                                            placeholder='Enter Title'
                                            value={formik.values.title}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            isInvalid={
                                                !!formik.errors.title && formik.touched.title
                                            }
                                        />

                                        <span className='text-red-500 text-xs'>
                                            {formik.touched.title && formik.errors.title}
                                        </span>
                                    </div>
                                </div>
                                <div className='col-12 col-sm-6'>
                                    <div className='form-group login-input-section'>
                                        <label htmlFor='title'>Detail</label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            className='form-control'
                                            id='detail'
                                            name='detail'
                                            placeholder='Enter Detail'
                                            value={formik.values.detail}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            isInvalid={!!formik.errors.detail && formik.touched.detail}
                                        />
                                        <span className='text-red-500 text-xs'>
                                            {formik.touched.detail && formik.errors.detail}
                                        </span>
                                    </div>
                                </div>

                                <div className='col-12 col-sm-6 mt-3'> {/* Added mt-3 for spacing */}
                                    <div className='form-group login-input-section'>
                                        <label htmlFor='user_type'>Notification Type</label>
                                        <Form.Control
                                            as='select'
                                            className='form-control'
                                            id='user_type'
                                            name='user_type'
                                            value={formik.values.user_type}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                if (e.target.value !== "1") {
                                                    formik.setFieldValue('user_id', '');
                                                }
                                                if (e.target.value !== "2") {
                                                    formik.setFieldValue('owner_id', '');
                                                    formik.setFieldValue('sendTo', false);
                                                }
                                                if (e.target.value !== "3") {
                                                    formik.setFieldValue('location_id', '');
                                                    formik.setFieldValue('is_admin', true);
                                                    formik.setFieldValue('is_owner', true);
                                                    formik.setFieldValue('is_viewer', true);
                                                }
                                                if (e.target.value !== "4") {
                                                    formik.setFieldValue('sensor_id', '');
                                                    formik.setFieldValue('is_admin', true);
                                                    formik.setFieldValue('is_owner', true);
                                                    formik.setFieldValue('is_viewer', true);
                                                }
                                                if(e.target.value !== "4" && e.target.value !== "3"){
                                                    formik.setFieldValue('distance', 1);
                                                }
                                            }}
                                            onBlur={formik.handleBlur}
                                            isInvalid={!!formik.errors.user_type && formik.touched.user_type}
                                        >
                                            {notificationTargets.map(target => (
                                                <option key={target.value} value={target.value}>
                                                    {target.label}
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <span className='text-red-500 text-xs'>
                                            {formik.touched.user_type && formik.errors.user_type}
                                        </span>
                                    </div>
                                </div>

                                {formik.values.user_type === "1" && (
                                    <div className='col-12 col-sm-6 mt-3'>
                                        <div className='form-group login-input-section'>
                                            <label htmlFor='user_id'>Select User</label>
                                            <Form.Control
                                                as='select'
                                                className='form-control'
                                                id='user_id'
                                                name='user_id'
                                                value={formik.values.user_id}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={!!formik.errors.user_id && formik.touched.user_id}
                                            >
                                                {/* Add a disabled default option */}
                                                <option value="" disabled>Select a User</option>

                                                {/* Fix the customer mapping and rendering */}
                                                {customers.map((customer: CustomerDetail) => (
                                                    // Assuming customer._id is the actual ID for the backend
                                                    <option key={customer.userId} value={customer.userId}>
                                                        {`${customer.first_name} ${customer.last_name}`}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                            <span className='text-red-500 text-xs'>
                                                {formik.touched.user_id && formik.errors.user_id}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {formik.values.user_type === "2" && (
                                    <div className='col-12 col-sm-6 mt-3'>
                                        <div className='form-group login-input-section'>
                                            <label htmlFor='user_id'>Select Owner</label>
                                            <Form.Control
                                                as='select'
                                                className='form-control'
                                                id='owner_id'
                                                name='owner_id'
                                                value={formik.values.owner_id}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={!!formik.errors.owner_id && formik.touched.owner_id}
                                            >
                                                {/* Add a disabled default option */}
                                                <option value="" disabled>Select a Owner</option>

                                                {/* Fix the customer mapping and rendering */}
                                                {owners.map((owner: SubscriptionData) => (
                                                    // Assuming customer._id is the actual ID for the backend
                                                    <option key={owner.userId} value={owner.userId}>
                                                        {`${owner.first_name} ${owner.last_name}`}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                            <span className='text-red-500 text-xs'>
                                                {formik.touched.owner_id && formik.errors.owner_id}
                                            </span>
                                        </div>
                                        <Form.Check
                                                type="checkbox"
                                                id="sendTo"
                                                name="sendTo"
                                                label="Exclude Viewers" // <-- Label property for better alignment
                                                checked={formik.values.sendTo}
                                                onChange={(e) => formik.setFieldValue("sendTo", e.target.checked)}
                                                onBlur={formik.handleBlur}
                                            />
                                    </div>
                                )}

                                {formik.values.user_type === "3" && (
                                    <>
                                        <div className='col-12 col-sm-6 mt-3'>
                                            <div className='form-group login-input-section'>
                                                <label htmlFor='user_id'>Select Location</label>
                                                <Form.Control
                                                    as='select'
                                                    className='form-control'
                                                    id='location_id'
                                                    name='location_id'
                                                    value={formik.values.location_id}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    isInvalid={!!formik.errors.location_id && formik.touched.location_id}
                                                >
                                                    {/* Add a disabled default option */}
                                                    <option value="" disabled>Select a Location</option>

                                                    {/* Fix the customer mapping and rendering */}
                                                    {locations.map((location: any) => (
                                                        // Assuming customer._id is the actual ID for the backend
                                                        <option key={location.id} value={location.id}>
                                                            {`${location.name}`}
                                                        </option>
                                                    ))}
                                                </Form.Control>
                                                <span className='text-red-500 text-xs'>
                                                    {formik.touched.location_id && formik.errors.location_id}
                                                </span>
                                            </div>
                                            <div className='form-group login-input-section'>
                                            <label htmlFor='title'>Distance(in Kms)</label>
                                            <Form.Control
                                                type='number'
                                                className='form-control'
                                                id='distance'
                                                name='distance'
                                                placeholder='Enter Distance'
                                                value={formik.values.distance}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={
                                                    !!formik.errors.distance && formik.touched.distance
                                                }
                                            />

                                            <span className='text-red-500 text-xs'>
                                                {formik.touched.distance && formik.errors.distance}
                                            </span>
                                        </div>
                                            <Form.Check
                                                type="checkbox"
                                                id="is_admin"
                                                name="is_admin"
                                                label="Admin" // <-- Label property for better alignment
                                                checked={formik.values.is_admin}
                                                onChange={(e) => formik.setFieldValue("is_admin", e.target.checked)}
                                                onBlur={formik.handleBlur}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="is_owner"
                                                name="is_owner"
                                                label="Owner" // <-- Label property for better alignment
                                                checked={formik.values.is_owner}
                                                onChange={(e) => formik.setFieldValue("is_owner", e.target.checked)}
                                                onBlur={formik.handleBlur}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="is_viewer"
                                                name="is_viewer"
                                                label="Viewer" // <-- Label property for better alignment
                                                checked={formik.values.is_viewer}
                                                onChange={(e) => formik.setFieldValue("is_viewer", e.target.checked)}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                    </>
                                )}

                                {formik.values.user_type === "4" && (
                                    <div className='col-12 col-sm-6 mt-3'>
                                        <div className='form-group login-input-section'>
                                            <label htmlFor='user_id'>Select Sensor</label>
                                            <Form.Control
                                                as='select'
                                                className='form-control'
                                                id='sensor_id'
                                                name='sensor_id'
                                                value={formik.values.sensor_id}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={!!formik.errors.sensor_id && formik.touched.sensor_id}
                                            >
                                                {/* Add a disabled default option */}
                                                <option value="" disabled>Select a Sensor</option>

                                                {/* Fix the customer mapping and rendering */}
                                                {sensors.map((sensor: any) => (
                                                    // Assuming customer._id is the actual ID for the backend
                                                    <option key={sensor.id} value={sensor.id}>
                                                        {`${sensor.name}`}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                            <span className='text-red-500 text-xs'>
                                                {formik.touched.sensor_id && formik.errors.sensor_id}
                                            </span>
                                        </div>
                                        <div className='form-group login-input-section'>
                                            <label htmlFor='title'>Distance(in Kms)</label>
                                            <Form.Control
                                                type='number'
                                                className='form-control'
                                                id='distance'
                                                name='distance'
                                                placeholder='Enter Distance'
                                                value={formik.values.distance}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={
                                                    !!formik.errors.distance && formik.touched.distance
                                                }
                                            />

                                            <span className='text-red-500 text-xs'>
                                                {formik.touched.distance && formik.errors.distance}
                                            </span>
                                        </div>
                                            <Form.Check
                                                type="checkbox"
                                                id="is_admin"
                                                name="is_admin"
                                                label="Admin"
                                                checked={formik.values.is_admin}
                                                onChange={(e) => formik.setFieldValue("is_admin", e.target.checked)}
                                                onBlur={formik.handleBlur}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="is_owner"
                                                name="is_owner"
                                                label="Owner"
                                                checked={formik.values.is_owner}
                                                onChange={(e) => formik.setFieldValue("is_owner", e.target.checked)}
                                                onBlur={formik.handleBlur}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="is_viewer"
                                                name="is_viewer"
                                                label="Viewer"
                                                checked={formik.values.is_viewer}
                                                onChange={(e) => formik.setFieldValue("is_viewer", e.target.checked)}
                                                onBlur={formik.handleBlur}
                                            />
                                    </div>
                                )}

                            </div>
                            <div className='col-xl-8 col-lg-10 col-md-10 col-12 mt-4 w-100'>
                                <div className='d-flex gap-3 justify-content-center'>
                                    <div className='d-flex gap-3'>
                                        <PrimaryButton
                                            type='submit'
                                            label={loading ? 'Submitting...' : "Submit"}
                                            className='editFormButton w-100'
                                            disabled={loading}
                                        />
                                        <SecondaryButton
                                            type='button'
                                            onClick={() => {
                                                formik.resetForm()
                                            }}
                                            label='Reset'
                                            className='editFormButton w-100'
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div></section>
    )
}

export default SendNotification